import { useEffect, useState } from "react";
import { api, errorMessage, fieldErrors } from "@/lib/api";
import { DataTable } from "@/lib/admin";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/perms";
import { loadSite } from "@/lib/site";
import { PasswordInput } from "@/components/form/PasswordInput";
import { PageHead } from "@/pages/admin/AdminLayout";
import { Notice } from "@/components/form/FormFeedback";
import { FieldError } from "@/components/form/ConfigField";
import { Loading } from "@/components/layout/RequireAuth";
import { ReportButtons } from "@/components/admin/ReportButtons";
import { usePageTitle } from "@/components/layout/AppLayout";

export const Input = ({ id, label, error, as = "input", children, ...p }) => {
    const Tag = as === "input" && p.type === "password" ? PasswordInput : as;
    return (
        <div>
            <label htmlFor={id} className="edi-label">{label}</label>
            <Tag id={id} className="edi-input" aria-invalid={error ? "true" : "false"} aria-describedby={error ? `${id}-error` : undefined} data-testid={`${id}-input`} {...p}>{children}</Tag>
            <FieldError id={`${id}-error`} error={error} />
        </div>
    );
};

const EMPTY_SEDE = { name: "", address: "", city: "", cap: "", phone: "", email: "", region: "", provinces: [], active: true };

export const Sedi = () => {
    usePageTitle("Sedi");
    const { user } = useAuth();
    const edit = can(user, "sedi", "M");
    const [items, setItems] = useState(null);
    const [provinces, setProvinces] = useState([]);
    const [form, setForm] = useState(null);
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState(null);
    const load = () => api.get("/admin/sedi").then(({ data }) => setItems(data.items));
    useEffect(() => { load(); api.get("/provinces").then(({ data }) => setProvinces(data.items)); }, []);
    if (!items) return <Loading />;
    const regions = [...new Set(provinces.map((p) => p.region))].sort();
    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const save = async (e) => {
        e.preventDefault();
        try {
            if (form.id) await api.put(`/admin/sedi/${form.id}`, form); else await api.post("/admin/sedi", form);
            setForm(null); setErrors({}); setMsg({ kind: "success", text: "Sede salvata" }); load();
        } catch (err) { setErrors(fieldErrors(err) || {}); if (!fieldErrors(err)) setMsg({ kind: "error", text: errorMessage(err) }); }
    };
    const act = async (fn) => { try { await fn(); load(); setMsg(null); } catch (err) { setMsg({ kind: "error", text: errorMessage(err) }); } };
    return (
        <>
            <PageHead title="Sedi"><ReportButtons section="sedi" />{edit && <button type="button" className="btn-primary py-2" onClick={() => { setForm({ ...EMPTY_SEDE }); setErrors({}); }} data-testid="sede-create-button">Nuova sede</button>}</PageHead>
            {msg && <div className="mb-4"><Notice kind={msg.kind} role={msg.kind === "error" ? "alert" : "status"} testId="sedi-message">{msg.text}</Notice></div>}
            {form && (
                <form noValidate onSubmit={save} className="edi-card p-5 mb-6 grid gap-4 sm:grid-cols-2" data-testid="sede-form">
                    <h2 className="sm:col-span-2 text-xl font-bold">{form.id ? "Modifica sede" : "Nuova sede"}</h2>
                    {[["name", "Nome"], ["address", "Indirizzo"], ["city", "Città"], ["cap", "CAP"], ["phone", "Telefono"], ["email", "Email"]].map(([k, l]) => (
                        <Input key={k} id={`sede-${k}`} label={l} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} error={errors[k]} />
                    ))}
                    <Input as="select" id="sede-region" label="Regione" value={form.region} onChange={(e) => set("region", e.target.value)} error={errors.region}>
                        <option value="">Seleziona…</option>{regions.map((r) => <option key={r}>{r}</option>)}
                    </Input>
                    <label className="flex items-center gap-2 self-end"><input type="checkbox" className="h-5 w-5" checked={form.active} onChange={(e) => set("active", e.target.checked)} data-testid="sede-active-input" /> Sede attiva</label>
                    <details className="sm:col-span-2" open><summary className="font-medium cursor-pointer">Province coperte ({form.provinces.length})</summary>
                        <fieldset className="mt-2 grid gap-1 grid-cols-2 md:grid-cols-4 max-h-64 overflow-y-auto"><legend className="sr-only">Province coperte</legend>
                            {provinces.map((p) => <label key={p.sigla} className="flex gap-2 items-center text-sm"><input type="checkbox" checked={form.provinces.includes(p.sigla)} data-testid={`sede-prov-${p.sigla}`}
                                onChange={() => set("provinces", form.provinces.includes(p.sigla) ? form.provinces.filter((x) => x !== p.sigla) : [...form.provinces, p.sigla])} />{p.name} ({p.sigla})</label>)}
                        </fieldset></details>
                    <div className="sm:col-span-2 flex gap-3"><button type="submit" className="btn-primary" data-testid="sede-save-button">Salva</button><button type="button" className="btn-secondary" onClick={() => setForm(null)}>Annulla</button></div>
                </form>
            )}
            <DataTable caption={`Sedi SPIN (${items.length})`} testId="sedi-table" rows={items} columns={[
                { key: "name", label: "Nome" }, { key: "city", label: "Città" }, { key: "region", label: "Regione" },
                { key: "provinces", label: "Province", render: (r) => r.provinces.join(", ") }, { key: "companies", label: "Aziende" },
                { key: "active", label: "Stato", render: (r) => (r.active ? "Attiva" : "Disattivata") },
                ...(edit ? [{ key: "actions", label: "Azioni", render: (r) => (
                    <div className="flex flex-wrap gap-2">
                        <button type="button" className="underline" onClick={() => { setForm({ ...EMPTY_SEDE, ...r, phone: r.phone || "", email: r.email || "" }); setErrors({}); }} data-testid={`sede-edit-${r.id}`}>Modifica</button>
                        <button type="button" className="underline" onClick={() => act(() => api.put(`/admin/sedi/${r.id}`, { ...r, phone: r.phone || "", email: r.email || "", active: !r.active }))} data-testid={`sede-toggle-${r.id}`}>{r.active ? "Disattiva" : "Riattiva"}</button>
                        <button type="button" className="underline text-edi-error" onClick={() => act(() => api.delete(`/admin/sedi/${r.id}`))} data-testid={`sede-delete-${r.id}`}>Elimina</button>
                    </div>) }] : []),
            ]} />
        </>
    );
};

const FOOTER = [["footer_descrizione", "Descrizione", "textarea"], ["footer_indirizzo", "Indirizzo"], ["footer_telefono", "Telefono", "tel"],
    ["footer_email", "Email", "email"], ["footer_pec", "PEC (facoltativo)", "email"], ["footer_sito", "Sito web (facoltativo)", "url"]];

export const Impostazioni = () => {
    usePageTitle("Impostazioni");
    const [s, setS] = useState(null);
    const [errors, setErrors] = useState({});
    const [msg, setMsg] = useState("");
    useEffect(() => { api.get("/admin/settings").then(({ data }) => setS(data)); }, []);
    if (!s) return <Loading />;
    const save = async (e) => {
        e.preventDefault(); setMsg("");
        try { const { data } = await api.put("/admin/settings", s); setErrors({}); setMsg(data.message); loadSite(); }
        catch (err) { setErrors(fieldErrors(err) || {}); setMsg("Correggi i campi evidenziati"); }
    };
    const set = (k) => (e) => setS({ ...s, [k]: e.target.value });
    return (
        <>
            <PageHead title="Impostazioni" />
            <form noValidate onSubmit={save} className="space-y-6 max-w-3xl" data-testid="settings-form">
                <fieldset className="edi-card p-5 space-y-5">
                    <legend className="sr-only">Assistente AI</legend>
                    <h2 className="text-xl font-bold" aria-hidden="true">Assistente AI</h2>
                    <Input as="textarea" id="set-widget" label="Codice di incorporamento dell'assistente AI" rows={5} value={s.widget_embed_code} onChange={set("widget_embed_code")} error={errors.widget_embed_code} />
                    <p className="edi-help -mt-3">Incolla lo script fornito dal servizio di chat. Lascia vuoto per disattivare l'assistente.</p>
                    <Input as="textarea" id="set-notice" label="Informativa sull'assistente AI (Privacy e consensi)" rows={6} value={s.ai_privacy_notice} onChange={set("ai_privacy_notice")} error={errors.ai_privacy_notice} />
                </fieldset>
                <fieldset className="edi-card p-5 space-y-5">
                    <legend className="sr-only">Privacy policy</legend>
                    <h2 className="text-xl font-bold" aria-hidden="true">Privacy policy</h2>
                    <Input id="set-privacy-url" type="url" inputMode="url" label="Link Privacy policy (URL)" placeholder="https://" value={s.privacy_policy_url} onChange={set("privacy_policy_url")} error={errors.privacy_policy_url} />
                    <p className="edi-help -mt-3">Usato nel piè di pagina e nella casella privacy della registrazione (si apre in una nuova scheda). Se vuoto si usa la pagina interna.</p>
                </fieldset>
                <fieldset className="edi-card p-5 space-y-5">
                    <legend className="sr-only">Piè di pagina: informazioni e contatti</legend>
                    <h2 className="text-xl font-bold" aria-hidden="true">Piè di pagina: informazioni e contatti</h2>
                    {FOOTER.map(([k, l, t]) => (
                        <Input key={k} id={`set-${k}`} label={l} {...(t === "textarea" ? { as: "textarea", rows: 3 } : { type: t === "url" ? "url" : t || "text" })} value={s[k] || ""} onChange={set(k)} error={errors[k]} />
                    ))}
                </fieldset>
                <button type="submit" className="btn-primary" data-testid="settings-save-button">Salva</button>
                <p role="status" aria-live="polite" data-testid="settings-status">{msg}</p>
            </form>
        </>
    );
};
